import { Badge, Button, Group, Image, Modal, Paper, Stack, Text, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconBell, IconPlus } from '@tabler/icons-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { PaginationState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { Table } from '@/components/Table'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { PushNotificationHistoryItem, PushNotificationStatus } from '@/services/api/api.types'
import type { TableColumnDef } from '@/utils/table'

import { CreateNotificationModal } from './CreateNotificationModal'
import { formatSentAt, HISTORY_PAGE_SIZE } from './notifications'

const noHistory: PushNotificationHistoryItem[] = []

const statusBadges: Record<PushNotificationStatus, { label: string; color: string }> = {
  sending: { label: 'Sending', color: 'blue' },
  completed: { label: 'Sent', color: 'green' },
  failed: { label: 'Not sent', color: 'red' },
}

export function NotificationsPanel() {
  const { api } = useGlobalState()
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: HISTORY_PAGE_SIZE })

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'history', pagination],
    queryFn: () => api.getPushNotificationHistory({ page: pagination.pageIndex + 1, page_size: pagination.pageSize }),
    placeholderData: keepPreviousData,
    refetchInterval: query => (query.state.data?.items.some(item => item.status === 'sending') ? 5000 : false),
  })

  const columns = useMemo<TableColumnDef<PushNotificationHistoryItem>[]>(
    () => [
      {
        header: 'Title',
        accessorKey: 'title',
        size: 200,
        cell: ({ getValue }) => (
          <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
            {getValue<string>()}
          </Text>
        ),
      },
      {
        header: 'Message',
        accessorKey: 'body',
        size: 320,
        cell: ({ getValue }) => (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {getValue<string>()}
          </Text>
        ),
      },
      {
        header: 'Image',
        accessorKey: 'imageUrl',
        size: 90,
        meta: { grow: false },
        cell: ({ getValue }) => {
          const imageUrl = getValue<string | null>()
          return imageUrl ? (
            <UnstyledButton onClick={() => setEnlargedImageUrl(imageUrl)} aria-label="View image">
              <Image src={imageUrl} alt="" w={48} h={48} radius="sm" fit="cover" />
            </UnstyledButton>
          ) : null
        },
      },
      {
        header: 'Sent By',
        accessorKey: 'senderName',
        size: 220,
        cell: ({ getValue }) => (
          <Text size="sm" truncate="end" title={getValue<string | null>() ?? undefined}>
            {getValue<string | null>()}
          </Text>
        ),
      },
      {
        header: 'Sent',
        accessorKey: 'sentAt',
        size: 220,
        meta: { grow: false },
        cell: ({ getValue }) => <Text size="sm">{formatSentAt(getValue<string>())}</Text>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 110,
        meta: { grow: false },
        cell: ({ getValue }) => {
          const badge = statusBadges[getValue<PushNotificationStatus>()]
          return badge ? (
            <Badge color={badge.color} variant="light">
              {badge.label}
            </Badge>
          ) : null
        },
      },
    ],
    [],
  )

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="md" c="dimmed">
          Send a push notification to every End User in your channel. Notifications go out right away and can&apos;t be
          recalled or edited.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate} style={{ flexShrink: 0 }}>
          Create Notification
        </Button>
      </Group>

      <Paper p="lg" shadow="sm" radius="md">
        <Table
          columns={columns}
          data={data?.items ?? noHistory}
          getRowId={row => row.campaignId}
          isLoading={isLoading}
          entity={{ singular: 'Notification', plural: 'Notifications', Icon: IconBell }}
          enableTopToolbar={false}
          pagination={{ state: pagination, rowCount: data?.total ?? 0, onChange: setPagination }}
        />
      </Paper>

      <CreateNotificationModal opened={createOpened} onClose={closeCreate} />

      <Modal opened={!!enlargedImageUrl} onClose={() => setEnlargedImageUrl(null)} title="Notification image" centered>
        {enlargedImageUrl && <Image src={enlargedImageUrl} alt="" radius="sm" />}
      </Modal>
    </Stack>
  )
}
